import React, {useCallback, useEffect, useMemo, useState} from "react";
import Button from "../../../../common/components/Button";
import { AutoNextOnTimeout } from "./AutoNextOnTimeout";
import { readDirectoryFilesUrls } from "../../../../common/firebase/storage";
import { LoadingOutlined } from "@ant-design/icons";
import T from "../../../../common/i18n/Text";
import {IPresentationGameConfig, Lang, Localised, LocalStorage} from "../../../../common/types";
import { getCurrentLang } from "../../../../common/utils/lang";
import PlayScreen from "../../../../common/components/game-screens/PlayScreen";
import GameImage from "../../../../common/components/GameImage";
import {getNextRandomLocalisedAndSaveAsUsed} from "../../../../common/utils/storage";

interface Props {
    config: IPresentationGameConfig;
    showInfo: () => void;
    showConfig: () => void;
}

class Slide {
    path: string;
    instruction: Localised<string>;

    constructor(
        path: string,
        instruction: Localised<string>
    ) {
        this.path = path;
        this.instruction = instruction;
    }
}

const MAX_CACHE_TIME = 1000 * 60 * 60 * 24;

const topicsData:Localised<string>[] = require("../topics.json");
const instructionsData:Localised<string>[] = require("../instructions.json");

export const PlayComponent = ({ config, showInfo, showConfig }: Props) => {
    const [slideLinks, setSlideLinks] = useState<string[]>([]);

    const [slides, setSlides] = useState<Slide[]>([]);
    const [currentSlide, setCurrentSlide] = useState<number>(0);

    const [replayAmount, changeReplayAmount] = useState<number>(0);
    const replay = () => {
        changeReplayAmount(replayAmount + 1);
        setCurrentSlide(0);
    };

    const activeLang:Lang = getCurrentLang();

    // load list of slide links
    useEffect(() => {
        const slidesStr = localStorage.getItem(LocalStorage.SLIDES_STORAGE);
        const hasToBeUpdated = shouldSlidesListBeUpdated();

        if (slidesStr === null || hasToBeUpdated) {
            readDirectoryFilesUrls(`games/presentation/`).then(links => {
                persistSlides(links);
                setSlideLinks(links);
            });
        } else {
            setSlideLinks(JSON.parse(slidesStr));
        }
    }, []);

    const getNextRandomSlide = useCallback(() => {
        let usedSlides = getUsedSlides();
        console.log(usedSlides)
        let unSeenSlides = slideLinks.filter(slide => !usedSlides.includes(slide));

        if (unSeenSlides.length === 0) {
            usedSlides = [];
            unSeenSlides = slideLinks;
        }

        const randomIndex = Math.floor(Math.random() * unSeenSlides.length);
        const slideLink = unSeenSlides[randomIndex];

        const instruction = config.showInstructions ? getNextRandomInstruction() : {ru: "", en: ""};

        saveUsedSlides([...usedSlides, slideLink]);

        return new Slide(slideLink, instruction);
    }, [slideLinks, config]);

    useEffect(() => {
        if (slideLinks.length === 0) return;

        const slides = [...Array(config.slideCount)].map(getNextRandomSlide);

        preloadSlides(slides);
        setSlides(slides);
    }, [config, slideLinks, setSlides, replayAmount, getNextRandomSlide]);

    const showNextSlide = () => {
        setCurrentSlide(currentSlide + 1);
    };

    const topic = useMemo(() => {
        console.log(replayAmount);
        return config.randomTopic ? getNextRandomTopic() : {ru: config.customTopic, en: config.customTopic};
    }, [config, replayAmount]);

    const slide =
        currentSlide === 0
            ? titleSlide(topic[activeLang])
            : currentSlide > config.slideCount
            ? thankYouSlide()
            : imageSlide(slides[currentSlide - 1], activeLang);

    const slideStep = (
        <div className="slide-step w-[55px] flex flex-row gap-1 items-center text-[16px] leading-[19px] font-medium">
            {Math.min(config.slideCount, Math.max(currentSlide, 1))} /{" "}
            {config.slideCount}
        </div>
    );

    const nextSlideButton = (
        <Button
            type="primary"
            onClick={showNextSlide}
            name="presentation-next"
            keyboardKeys={["ArrowRight", " ", "Enter"]}>
            <T path="games.buttons.next" />
        </Button>
    );
    const replayGameButton = (
        <Button
            type="primary"
            onClick={replay}
            name="presentation-replay"
            keyboardKeys={["ArrowRight", " ", "Enter"]}>
            <T path="games.buttons.replay" />
        </Button>
    );
    const autoNextSlide = (
        <AutoNextOnTimeout
            slide={currentSlide}
            showNextSlide={showNextSlide}
            duration={config.duration * 1000}
        />
    );
    const nextOrAutoLoad = config.autoNext ? autoNextSlide : nextSlideButton;
    const nextOrReplay =
        currentSlide > config.slideCount ? replayGameButton : nextOrAutoLoad;

    const loadingOrAction =
        slides.length === 0 ? <LoadingOutlined /> : nextOrReplay;

    return (
        <PlayScreen
            mainActions={loadingOrAction}
            additionalInfo={slideStep}
            showInfo={showInfo}
            showConfig={showConfig}>
            <div className="flex items-center justify-center">{slide}</div>
        </PlayScreen>
    );
};

const titleSlide = (title: string) => {
    return (
        <div className="slide-class flex flex-col justify-center items-center text-center px-6">
            <h1 className="text-3xl lg:text-6xl">{title}</h1>
        </div>
    );
};

const thankYouSlide = () => {
    return (
        <div className="slide-class flex flex-col justify-center items-center">
            <h1 className="text-6xl">
                <T path="games.presentation.thank-you" />
            </h1>
        </div>
    );
};

const imageSlide = (slide: Slide, activeLang: Lang) => {
    return <GameImage imgSrc={slide.path} imgTitle={slide.instruction[activeLang]} />;
};

const preloadSlides = (slides: Slide[]) => {
    slides.forEach((slide) => {
        let link = document.createElement("link");
        link.rel = "preload";
        link.as = "image";
        link.href = slide.path;
        document.head.appendChild(link);
    });
};

const shouldSlidesListBeUpdated = ():boolean => {
    const slidesStoreDateStr = localStorage.getItem(LocalStorage.SLIDES_STORAGE_DATE);
    const currentTime = new Date().getTime();

    return slidesStoreDateStr === null ||
        currentTime - new Date(slidesStoreDateStr).getTime() >
        MAX_CACHE_TIME;
};

const getNextRandomTopic = () => {
    return getNextRandomLocalisedAndSaveAsUsed(topicsData, LocalStorage.USED_TOPICS_STORAGE);
};

const getNextRandomInstruction = () => {
    return getNextRandomLocalisedAndSaveAsUsed(instructionsData, LocalStorage.USED_INSTRUCTIONS_STORAGE);
};

const getUsedSlides = ():string[] => {
    return JSON.parse(localStorage.getItem(LocalStorage.USED_SLIDES_STORAGE) || "[]");
};

const saveUsedSlides = (usedSlides: string[]) => {
    localStorage.setItem(LocalStorage.USED_SLIDES_STORAGE, JSON.stringify(usedSlides));
};

const persistSlides = (slides: string[]) => {
    localStorage.setItem(LocalStorage.SLIDES_STORAGE_DATE, new Date().toString());
    localStorage.setItem(LocalStorage.SLIDES_STORAGE, JSON.stringify(slides));
};
